import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogTitle } from "@mui/material";
import { useEventType } from "../../hooks/useEventType";
import { useEventTypeSpendTypes, useSpendType } from "../../hooks/useSpendType";
import { useRelationship } from "../../hooks/useRelationship";
import { useExternalSource } from "../../hooks/useExternalSource";
import { useCountry } from "../../hooks/useCountry";
import { useDivisionNameById } from "../../hooks/useDivision";
import { useTeamNameById } from "../../hooks/useTeam";
import { useSpendTypeActivityTypes } from "../../hooks/useActivityType";
import { useCountryCities } from "../../hooks/useCity";
import currencyData from "../../currencies.json";
import { useEditEvent } from "../../hooks/useEvent";
import { toast } from "react-toastify";
import { getEventStatus } from "./EventStatus";
import { useGetEventAttendees } from "../../hooks/useAttendee";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../../config/firebase";
import { useTeamsBrands } from "../../hooks/useBrand";
import { useBrandNameById } from "../../hooks/useBrand";

function UpdateEventDetails({ openEditModal, setOpenEditModal, eventData }) {
  const formatDate = (dateString) => {
    if (!dateString) return "";
    // Convert date from YYYY/MM/DD to YYYY-MM-DD
    return dateString.replace(/\//g, "-");
  };

  const [values, setValues] = useState({
    name: eventData?.name || "",
    event_type_id: eventData?.event_type_id || "",
    activity_type_id: eventData?.activity_type_id || "",
    country_id: eventData?.country_id || "",
    city_id: eventData?.city_id || "",
    spend_type_id: eventData?.spend_type_id || "",
    spend_value: eventData?.spend_value || "",
    currency: eventData?.currency || "",
    division_id: eventData?.division_id || "",
    team_id: eventData?.team_id || "",
    promo_approval_date: formatDate(eventData?.promo_approval_date) || "",
    promo_number: eventData?.promo_number || "",
    external_source_id: eventData?.external_source_id || "",
    relationship_id: eventData?.relationship_id || "",
    start_date: formatDate(eventData?.start_date) || "",
    end_date: formatDate(eventData?.end_date) || "",
    start_time: eventData?.start_time || "",
    end_time: eventData?.end_time || "",
    brand_id: eventData?.brand_id || "",
  });
  const [errors, setErrors] = useState({});

  const { data: eventTypeData } = useEventType();
  // const { data: spendTypeData } = useSpendType();
  const { data: relationshipData } = useRelationship();
  const { data: externalSourceData } = useExternalSource();
  const { data: countryData } = useCountry();
  const { data: divisionName } = useDivisionNameById(eventData?.division_id);
  const { data: teamName } = useTeamNameById(eventData?.team_id);
  const { data: brandName } = useBrandNameById(eventData?.brand_id);
  const { data: filteredSpendTypes } = useEventTypeSpendTypes(values.event_type_id);
  const { data: activityTypeData } = useSpendTypeActivityTypes(
    values?.spend_type_id
  );
  const { data: cityData } = useCountryCities(values?.country_id);
  const { data: attendeesData } = useGetEventAttendees(eventData?.id);

  const { mutate: editEvent } = useEditEvent();
  const sortedActivityTypeData = activityTypeData?.slice().sort((a, b) => {
    const numA = parseInt(a.name.match(/\d+/)?.[0] || 0, 10);
    const numB = parseInt(b.name.match(/\d+/)?.[0] || 0, 10);
    return numA - numB;
  });
  const sortedSpendTypeData = filteredSpendTypes?.slice().sort((a, b) =>
    a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: "base" })
  );

  const handleChange = (e) => {
    const { name, value } = e.target;
    setValues((prevValues) => ({
      ...prevValues,
      [name]: value,
    }));
  };

  const validateRules = {
    name: { required: "required name" },
    event_type_id: { required: true },
    activity_type_id: { required: true },
    country_id: { required: true },
    city_id: { required: true },
    spend_type_id: { required: true },
    spend_value: { required: false },
    currency: { required: true },
    promo_approval_date: { required: true },
    promo_number: { required: true },
    external_source_id: { required: true },
    relationship_id: { required: true },
    start_date: { required: true },
    end_date: { required: true },
    start_time: { required: true },
    end_time: { required: true },
  };

  const validateForm = () => {
    const newErrors = {};

    for (const field in validateRules) {
      if (validateRules[field].required && !values[field]) {
        errors[field] = "This field is required";
      }
    }
    setErrors(newErrors);
    return errors;
  };

  const validateDates = () => {
    if (values.start_date && values.end_date) {
      const start = new Date(values.start_date);
      const end = new Date(values.end_date);
      return end >= start;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate form
    const formErrors = validateForm();
    if (Object.keys(formErrors).length > 0) {
      toast.error("Please fill all required fields");
      return;
    }

    // Validate date logic
    if (!validateDates()) {
      toast.error("End date must be after start date");
      return;
    }

    // Format dates and prepare data
    const formattedValues = {
      ...values,
      start_date: values.start_date.replace(/-/g, "/"),
      end_date: values.end_date.replace(/-/g, "/"),
      promo_approval_date: values.promo_approval_date.replace(/-/g, "/"),
      spend_value: values.spend_value ? parseFloat(values.spend_value) : 0,
      updated_at: new Date().toISOString(),
    };

    // Check if data has changed
    const hasChanges = Object.keys(formattedValues).some(
      (key) => formattedValues[key] !== eventData[key]
    );

    if (!hasChanges) {
      toast.info("No changes were made");
      setOpenEditModal(false);
      return;
    }

    try {
      let updatedCityName = eventData.city_name || "";
      let updatedCountryName = eventData.country_name || "";

      if (values.city_id && values.city_id !== eventData.city_id) {
        const citySnap = await getDoc(doc(db, "cities", values.city_id));
        if (citySnap.exists()) {
          updatedCityName = citySnap.data().name || "";
        }
      }

      if (values.country_id && values.country_id !== eventData.country_id) {
        const countrySnap = await getDoc(
          doc(db, "countries", values.country_id)
        );
        if (countrySnap.exists()) {
          updatedCountryName = countrySnap.data().name || "";
        }
      }

      const currentStatus = getEventStatus(
        formattedValues.start_date,
        formattedValues.end_date,
        attendeesData,
        formattedValues.spend_value,
        eventData.organization_id,
        eventData.tov_exported
      );

      editEvent({
        id: eventData.id,
        data: {
          ...formattedValues,
          city_name: updatedCityName,
          country_name: updatedCountryName,
          status: currentStatus.text,
        },
      });

      toast.success("Event updated successfully");
      setOpenEditModal(false);
    } catch (error) {
      console.error("Error updating event:", error);
      toast.error("Failed to update event");
    }
  };

  return (
    <div>
      <Dialog
        open={openEditModal}
        onClose={() => setOpenEditModal(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle className="bg-blueColor text-white py-4">
          Edit Event Details
        </DialogTitle>
        <DialogContent className="mt-4">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4">
              <div>
                <label className="block text-gray-700 text-sm font-bold mb-2">
                  Event/Topic Name
                </label>
                <input
                  type="text"
                  name="name"
                  value={values.name}
                  onChange={handleChange}
                  className="w-full p-1.5 border rounded focus:outline-none focus:ring-2 focus:ring-purpleColor"
                />
              </div>
              <div>
                <label className="block text-gray-700 text-sm font-bold mb-2">
                  Event Type
                </label>
                <select
                  name="event_type_id"
                  value={values.event_type_id}
                  onChange={handleChange}
                  className="w-full p-2 border rounded focus:outline-none focus:ring-2 focus:ring-purpleColor"
                >
                  <option value="" hidden>
                    Select
                  </option>
                  {eventTypeData?.map((eventType) => (
                    <option key={eventType.id} value={eventType.id}>
                      {eventType.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-gray-700 text-sm font-bold mb-2">
                  Spend Type
                </label>
                <select
                  name="spend_type_id"
                  value={values.spend_type_id}
                  onChange={handleChange}
                  className="w-full p-2 border rounded focus:outline-none focus:ring-2 focus:ring-purpleColor"
                >
                  <option value="" hidden>
                    Select
                  </option>
                  {sortedSpendTypeData?.map((spendType) => (
                    <option key={spendType.id} value={spendType.id}>
                      {spendType.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-gray-700 text-sm font-bold mb-2">
                  City
                </label>
                <select
                  name="city_id"
                  value={values.city_id}
                  onChange={handleChange}
                  className="w-full p-2 border rounded focus:outline-none focus:ring-2 focus:ring-purpleColor"
                >
                  <option value="" hidden>
                    Select
                  </option>
                  {cityData?.map((city) => (
                    <option key={city.id} value={city.id}>
                      {city.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-gray-700 text-sm font-bold mb-2">
                  Spend Value
                </label>
                <input
                  type="number"
                  name="spend_value"
                  value={values.spend_value}
                  onChange={handleChange}
                  min="0"
                  step="any"
                  className="w-full p-1.5 border rounded focus:outline-none focus:ring-2 focus:ring-purpleColor"
                  placeholder="Enter spend value"
                />
                {errors.spend_value && (
                  <span className="text-red-500">{errors.spend_value}</span>
                )}
              </div>
              <div>
                <label className="block text-gray-700 text-sm font-bold mb-2">
                  Activity Type
                </label>
                <select
                  name="activity_type_id"
                  value={values.activity_type_id}
                  onChange={handleChange}
                  className="w-full p-2 border rounded focus:outline-none focus:ring-2 focus:ring-purpleColor"
                >
                  <option value="" hidden>
                    Select
                  </option>
                  {sortedActivityTypeData?.map((activityType) => (
                    <option key={activityType.id} value={activityType.id}>
                      {activityType.name}
                    </option>
                  ))}
                </select>
                {errors.activity_type_id && (
                  <span className="text-red-500">
                    {errors.activity_type_id}
                  </span>
                )}
              </div>
              <div>
                <label className="block text-gray-700 text-sm font-bold mb-2">
                  Currency
                </label>
                <select
                  name="currency"
                  value={values.currency}
                  onChange={handleChange}
                  className="w-full p-2 border rounded focus:outline-none focus:ring-2 focus:ring-purpleColor"
                >
                  <option value="" hidden>
                    Select
                  </option>
                  {currencyData.data?.map((currency, index) => (
                    <option key={index} value={currency.name}>
                      {currency.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-gray-700 text-sm font-bold mb-2">
                  Country
                </label>
                <select
                  name="country_id"
                  value={values.country_id}
                  onChange={handleChange}
                  className="w-full p-2 border rounded focus:outline-none focus:ring-2 focus:ring-purpleColor"
                >
                  <option value="" hidden>
                    Select
                  </option>
                  {countryData?.map((country) => (
                    <option key={country.id} value={country.id}>
                      {country.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-gray-700 text-sm font-bold mb-2">
                  City
                </label>
                <select
                  name="city_id"
                  value={values.city_id}
                  onChange={handleChange}
                  className="w-full p-2 border rounded focus:outline-none focus:ring-2 focus:ring-purpleColor"
                >
                  <option value="" hidden>
                    Select
                  </option>
                  {cityData?.map((city) => (
                    <option key={city.id} value={city.id}>
                      {city.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-gray-700 text-sm font-bold mb-2">
                  Division
                </label>
                <input
                  type="text"
                  name="division_id"
                  value={divisionName}
                  disabled
                  className="w-full p-1.5 border rounded focus:outline-none focus:ring-2 focus:ring-purpleColor bg-gray-100"
                />
              </div>

              <div>
                <label className="block text-gray-700 text-sm font-bold mb-2">
                  Brand
                </label>
                <input
                  type="text"
                  name="brand_id"
                  value={brandName}
                  disabled
                  className="w-full p-1.5 border rounded focus:outline-none focus:ring-2 focus:ring-purpleColor bg-gray-100"
                />
              </div>

              {/* <div>
                <label className="block text-gray-700 text-sm font-bold mb-2">
                  Brand
                </label>
                <select
                  name="brand_id"
                  value={values.brand_id}
                  disabled
                  onChange={handleChange}
                  className="w-full p-1.5 border rounded focus:outline-none focus:ring-2 focus:ring-purpleColor bg-gray-100"

                >
                  <option value="" hidden>
                    Select
                  </option>
                  {filteredBrands?.map((brand) => (
                    <option key={brand.id} value={brand.id}>
                      {brand.name}
                    </option>
                  ))}
                </select>
              </div> */}

              <div>
                <label className="block text-gray-700 text-sm font-bold mb-2">
                  Team
                </label>
                <input
                  type="text"
                  name="team_id"
                  value={teamName}
                  disabled
                  className="w-full p-1.5 border rounded focus:outline-none focus:ring-2 focus:ring-purpleColor bg-gray-100"
                />
              </div>

              <div>
                <label className="block text-gray-700 text-sm font-bold mb-2">
                  Promo/GPAS Approval Date
                </label>
                <input
                  type="date"
                  name="promo_approval_date"
                  value={values.promo_approval_date}
                  onChange={handleChange}
                  className="w-full p-1.5 border rounded focus:outline-none focus:ring-2 focus:ring-purpleColor"
                />
                {errors.promo_approval_date && (
                  <span className="text-red-500">
                    {errors.promo_approval_date}
                  </span>
                )}
              </div>

              <div>
                <label className="block text-gray-700 text-sm font-bold mb-2">
                  Promo/GPAS Number/UPI
                </label>
                <input
                  type="text"
                  name="promo_number"
                  value={values.promo_number}
                  onChange={handleChange}
                  className="w-full p-1.5 border rounded focus:outline-none focus:ring-2 focus:ring-purpleColor"
                />
              </div>

              <div>
                <label className="block text-gray-700 text-sm font-bold mb-2">
                  External Source System/Process
                </label>
                <select
                  name="external_source_id"
                  value={values.external_source_id}
                  onChange={handleChange}
                  className="w-full p-2 border rounded focus:outline-none focus:ring-2 focus:ring-purpleColor"
                >
                  <option value="" hidden>
                    Select
                  </option>
                  {externalSourceData?.map((externalSource) => (
                    <option key={externalSource.id} value={externalSource.id}>
                      {externalSource.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-gray-700 text-sm font-bold mb-2">
                  Relationship for the recipient
                </label>
                <select
                  name="relationship_id"
                  value={values.relationship_id}
                  onChange={handleChange}
                  className="w-full p-2 border rounded focus:outline-none focus:ring-2 focus:ring-purpleColor"
                >
                  <option value="" hidden>
                    Select
                  </option>
                  {relationshipData?.map((relationship) => (
                    <option key={relationship.id} value={relationship.id}>
                      {relationship.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-gray-700 text-sm font-bold mb-2">
                  Event Start Date
                </label>
                <input
                  type="date"
                  name="start_date"
                  value={values.start_date}
                  onChange={handleChange}
                  className="w-full p-1.5 border rounded focus:outline-none focus:ring-2 focus:ring-purpleColor"
                />
              </div>

              <div>
                <label className="block text-gray-700 text-sm font-bold mb-2">
                  Event End Date
                </label>
                <input
                  type="date"
                  name="end_date"
                  value={values.end_date}
                  onChange={handleChange}
                  className="w-full p-1.5 border rounded focus:outline-none focus:ring-2 focus:ring-purpleColor"
                />
              </div>

              <div>
                <label className="block text-gray-700 text-sm font-bold mb-2">
                  Event Start Time
                </label>
                <input
                  type="time"
                  name="start_time"
                  value={values.start_time}
                  onChange={handleChange}
                  className="w-full p-1.5 border rounded focus:outline-none focus:ring-2 focus:ring-purpleColor"
                />
              </div>

              <div>
                <label className="block text-gray-700 text-sm font-bold mb-2">
                  Event End Time
                </label>
                <input
                  type="time"
                  name="end_time"
                  value={values.end_time}
                  onChange={handleChange}
                  className="w-full p-1.5 border rounded focus:outline-none focus:ring-2 focus:ring-purpleColor"
                />
              </div>
            </div>

            <div className="flex justify-end gap-4 mt-6 p-4">
              <button
                type="button"
                onClick={() => setOpenEditModal(false)}
                className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded"
              >
                Cancel
              </button>
              <button
                data-track="edit-event-button"
                data-module="Events"
                type="submit"
                className="px-4 py-2 bg-blueColor text-white rounded"
              >
                Save Changes
              </button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default UpdateEventDetails;